import {
  // eslint-disable-next-line no-restricted-imports -- href() targets the locale-independent /api/v2/upcoming-events resource route, which needs no locale preservation
  href,
  useFetcher,
  useLocation,
  useNavigation,
  useRouteLoaderData,
  useSearchParams,
  type ShouldRevalidateFunctionArgs,
} from 'react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { LinkButton } from '@understory-io/leaf/link-button';
import { Stack } from '@understory-io/leaf/stack';
import { css } from '@understory-io/leaf/css';
import { Button } from '@understory-io/leaf/button';
import { Divider } from '@understory-io/leaf/divider';
import { FormattedMessage, useIntl } from 'react-intl';
import { cx } from '~/styled-system/css';
import { maxWidthContent } from '~/ui/app-shell/shared';
import type { Route } from './+types/route';
import type { RouteHandle } from '~/lib/header-config';
import type { RootLoaderData } from '~/root';
import { buildPageSeo } from '~/lib/seo';
import { loadHomePage } from './loader.server';
import { PageCard } from '~/ui/app-shell/layouts';
import {
  UpcomingDatesSection,
  type UpcomingDateAggregateEvent,
} from '~/ui/upcoming-dates-section';
import { HostCard } from '~/ui/host-card';
import { RatingBadge } from '~/ui/rating-badge';
import { PrivateEventCta } from '~/ui/private-event-cta';
import { ArrowUpRightIcon } from '~/ui/icons/arrow-up-right';
import { SearchIcon } from '~/ui/icons/search';
import { localeHref } from '~/lib/use-locale-href';
import { Hero } from '~/ui/hero';
import { ExperienceCard, experienceGridImageSizes } from '~/ui/experience-card';
import { ExperienceCardDates } from './components/experience-card-dates';
import {
  desktopColumnCount,
  ExperienceGrid,
  priorityImageIds,
} from './components/experience-grid';
import { ReviewsSection } from './components/reviews-section';
import { experienceFilterSearch } from '~/lib/routing/experience-link';
import {
  CategoryFilter,
  FILTER_SHEET_MESSAGES,
  FilterBar,
  LanguageFilter,
  LocationFilter,
  pick,
  type FilterSheetField,
  type LanguageOption,
  type LocationOption,
} from '~/ui/filters';
import { filterExperiences } from './filter-experiences';
import {
  DeferredFilterSheet as FilterSheet,
  usePreloadFilterSheet,
} from '~/ui/deferred/deferred-menus';
import { CrossSellSection } from '~/ui/cross-sell-section';
import { EmptyState } from './components/empty-state';

export const handle = {
  // Mobile: continue the raised "hero panel" up through the header strip
  // (see `heroPanelStyles` below). Tablet+ keeps `surface.base`.
  header: { surface: 'raised' },
} satisfies RouteHandle;

// The website-bound host proxy lives in the root `proxyBoundHostMiddleware`
// (see `app/root.tsx`); `/` on a bound host is proxied there before this route
// runs. Nothing to do here.

export async function loader({ params, url, context }: Route.LoaderArgs) {
  return loadHomePage({ params, url, context });
}

/**
 * Skip the heavy home loader on filter-only URL changes. The cards filter
 * client-side and the upcoming-events fetcher (resource route) handles the
 * events refetch, so re-running the full loader (experiences + variants +
 * prices + next-events + aggregator) just to re-render the same data is wasted
 * work. Any change OUTSIDE the filter keys (or a pathname change) revalidates
 * as usual. Keys are PLURAL, matching the repeated-key URL contract.
 */
export function shouldRevalidate({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) {
  const FILTER_KEYS = ['locations', 'languages', 'tags'];
  if (currentUrl.pathname !== nextUrl.pathname) return defaultShouldRevalidate;
  const stripped = (u: URL) => {
    const p = new URLSearchParams(u.search);
    FILTER_KEYS.forEach((k) => p.delete(k));
    return p.toString();
  };
  // Only filter params changed → don't re-run the heavy loader (cards filter
  // client-side; the upcoming-events fetcher handles the events refetch).
  if (stripped(currentUrl) === stripped(nextUrl)) return false;
  return defaultShouldRevalidate;
}

export function meta({ loaderData, matches }: Route.MetaArgs) {
  const root = matches.find((m) => m?.id === 'root')?.loaderData as
    RootLoaderData | undefined;
  if (!root) return [];

  return buildPageSeo({
    storefront: {
      fqdn: root.fqdn,
      languages: root.languages,
      defaultLanguage: root.defaultLanguage,
      resolvedLocale: root.locale,
      companyName: root.companyName,
      preview: root.preview,
      seoEnabled: root.preferences.seo,
    },
    buildPath: (locale) => localeHref(locale, '/:locale?'),
    // Empty page title → the `<title>` is just the company name.
    pageTitle: '',
    description: loaderData?.company.description,
    ogImage: loaderData?.company.coverImage,
  });
}

export default function Home({ loaderData, params }: Route.ComponentProps) {
  const rootLoaderData = useRouteLoaderData<RootLoaderData>('root');
  const {
    experiences,
    upcomingEvents,
    tags,
    availableLocations,
    availableLanguages,
    company,
    preferences,
    rating,
    reviews,
    renderedAt,
    datesByExperience,
  } = loaderData;

  // Filter state lives in the URL — `useSearchParams` reads it and writing
  // back replaces the URL (without re-running the heavy loader, see
  // `shouldRevalidate`). Cards are filtered client-side from the full set
  // (instant), and the upcoming-events carousel is refetched via a fetcher
  // against the resource route (targeted). The URL stays the single committed
  // source of truth, so the page is shareable and back/forward-friendly.
  //
  // URL keys use REPEATED entries (`?locations=a&locations=b&tags=t1`), read
  // via `searchParams.getAll(...)` — matching the loader + resource route.
  const [searchParams, setSearchParams] = useSearchParams();
  const navigation = useNavigation();
  const location = useLocation();
  // Prefer the in-flight navigation's target URL so filter checkboxes, card
  // filtering, and the trigger label all update instantly on click instead of
  // waiting ~200ms for the root/layout loaders to complete. React Router
  // clears `navigation.location` on commit or abort, so this is automatically
  // rolled back if the navigation is cancelled — no manual state needed.
  //
  // ONLY when the pending navigation stays on this page (a filter change). An
  // outgoing nav (e.g. clicking an experience card) targets a different path
  // with no filter params — reading those would blank the selection and flash
  // every card in for a frame before unmount, so we keep the committed filters.
  const pendingPathname = navigation.location?.pathname;
  const pendingSearch = navigation.location?.search;
  const activeParams = useMemo(() => {
    if (pendingPathname === location.pathname && pendingSearch !== undefined) {
      return new URLSearchParams(pendingSearch);
    }
    return searchParams;
  }, [pendingPathname, pendingSearch, location.pathname, searchParams]);
  const selectedLocationIds = useMemo(
    () => activeParams.getAll('locations'),
    [activeParams]
  );
  const selectedLanguages = useMemo(
    () => activeParams.getAll('languages'),
    [activeParams]
  );
  // Narrowed to the tags this page can name, so the trigger's label and the
  // cards it hides always agree — an id with no option filters nothing rather
  // than emptying the grid under an "All" label.
  const selectedTagIds = useMemo(
    () => pick(tags, activeParams.getAll('tags')).map((tag) => tag.id),
    [tags, activeParams]
  );

  const setMultiSelect = (
    updates: Partial<Record<'locations' | 'languages' | 'tags', string[]>>
  ) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, values] of Object.entries(updates)) {
          next.delete(key);
          for (const value of values) next.append(key, value);
        }
        return next;
      },
      { replace: true, preventScrollReset: true }
    );
  };

  const resetFilters = () =>
    setMultiSelect({ locations: [], languages: [], tags: [] });

  const [sheetOpen, setSheetOpen] = useState(false);
  usePreloadFilterSheet();
  const intl = useIntl();

  // When every option in a dimension is selected, treat it as no-filter — both
  // the empty and all-selected states present as "All" in the trigger, so they
  // must filter identically (show everything). The committed URL value stays
  // literal; only the derived filter/qs computations normalise here.
  // Wrapped in useMemo so the arrays are referentially stable, avoiding
  // unnecessary re-runs of the downstream useMemo hooks that depend on them.
  const effectiveLocationIds = useMemo(
    () =>
      selectedLocationIds.length === availableLocations.length
        ? []
        : selectedLocationIds,
    [selectedLocationIds, availableLocations.length]
  );
  const effectiveLanguages = useMemo(
    () =>
      selectedLanguages.length === availableLanguages.length
        ? []
        : selectedLanguages,
    [selectedLanguages, availableLanguages.length]
  );
  const effectiveTagIds = useMemo(
    () => (selectedTagIds.length === tags.length ? [] : selectedTagIds),
    [selectedTagIds, tags.length]
  );

  // Cards filter client-side from the FULL loader set — instant, no refetch.
  const visibleExperiences = useMemo(
    () =>
      filterExperiences(experiences, {
        locationIds: effectiveLocationIds,
        languages: effectiveLanguages,
        tagIds: effectiveTagIds,
      }),
    [experiences, effectiveLocationIds, effectiveLanguages, effectiveTagIds]
  );

  // Filtering can drop the grid to exactly two cards, which widens every card
  // — so the images' `sizes` has to follow the same count the grid does. That
  // count is also the size of the first row, which holds the LCP element (the
  // hero above it is text-only), so those images are fetched up front.
  const gridColumns = desktopColumnCount(visibleExperiences.length);
  const cardImageSizes = experienceGridImageSizes(gridColumns);
  const priorityImages = priorityImageIds(visibleExperiences, gridColumns);

  // ─── Upcoming events: targeted refetch via the resource route ───────────
  // The fetcher reloads only the events carousel when filters change, so the
  // heavy home loader stays skipped (see `shouldRevalidate`).
  const fetcher = useFetcher<{ events: UpcomingDateAggregateEvent[] }>();
  // The query string for the CURRENT selection, as repeated keys. Mirrors the
  // resource route's `getAll(...)` reads.
  const filterQs = useMemo(() => {
    const p = new URLSearchParams();
    for (const id of effectiveLocationIds) p.append('locations', id);
    for (const code of effectiveLanguages) p.append('languages', code);
    for (const id of effectiveTagIds) p.append('tags', id);
    return p.toString();
  }, [effectiveLocationIds, effectiveLanguages, effectiveTagIds]);
  // The query string SSR already loaded for — capture once so the FIRST effect
  // run skips the redundant fetch (the loader's `upcomingEvents` already
  // reflects this selection). A ref (not `filterQs === initialFilterQs`) so a
  // later return to the SSR baseline — e.g. clearing all filters — still
  // refetches rather than showing the previous filtered `fetcher.data`.
  // The same selection in the experience page's own param vocabulary, so
  // clicking a card keeps the visitor's narrowing instead of dropping them on
  // an arbitrary location's dates (US-11362).
  const experienceFilterQs = useMemo(
    () =>
      experienceFilterSearch({
        locationIds: effectiveLocationIds,
        languages: effectiveLanguages,
      }),
    [effectiveLocationIds, effectiveLanguages]
  );
  const [initialFilterQs] = useState(filterQs);
  const didInitialSkip = useRef(false);
  useEffect(() => {
    if (!didInitialSkip.current) {
      didInitialSkip.current = true;
      if (filterQs === initialFilterQs) return;
    }
    fetcher.load(`${href('/api/v2/upcoming-events')}?${filterQs}`);
    // `fetcher.load` is stable; re-running only when the query string changes
    // is intentional (the initial-skip is handled by the ref above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterQs]);
  // The experience page's booking calendar isn't part of this page's initial
  // JS, so fetch it once the page is idle: tapping a card then doesn't wait for
  // it on a slow connection.
  useEffect(() => {
    const prefetch = () => void import('~/ui/availability-calendar');
    if (typeof requestIdleCallback === 'function') {
      const id = requestIdleCallback(prefetch, { timeout: 5_000 });
      return () => cancelIdleCallback(id);
    }
    const id = setTimeout(prefetch, 2_000);
    return () => clearTimeout(id);
  }, []);

  const upcoming = fetcher.data?.events ?? upcomingEvents;
  const upcomingPending = fetcher.state !== 'idle';

  // "See all dates" link: carry the current filter selection as the same
  // repeated-key params used on the home URL. Re-uses the already-memoized
  // `filterQs` so no extra deps are needed. Omitted (no `?`) when no filters
  // are active. The upcoming-events page reads the same vocabulary, so the
  // selection survives the click.
  const seeAllDatesHref = useMemo(() => {
    const base = localeHref(params.locale, '/:locale?/upcoming-events');
    return filterQs ? `${base}?${filterQs}` : base;
  }, [params.locale, filterQs]);

  // Filter OPTION lists come from the loader (computed from the pre-slice event
  // set), NOT from the current `upcomingEvents` feed — so options aren't
  // dropped just because their events fell outside the top-N slice or the
  // active filter.
  const locationOptions: LocationOption[] = availableLocations;
  const languageOptions: LanguageOption[] = availableLanguages;

  // Mobile sheet dimensions, declared for the shared `FilterSheet`. The
  // labels come from `~/ui/filters`'s shared descriptors, whose wording is
  // unchanged from when they lived inside this page's own sheet — so their
  // content-hashed ids, and their translations, are untouched.
  const m = FILTER_SHEET_MESSAGES;
  const sheetFields: FilterSheetField[] = [
    {
      key: 'locations',
      label: intl.formatMessage(m.locationLabel),
      options: locationOptions.map((o) => ({ value: o.id, label: o.label })),
      value: selectedLocationIds,
      emptyLabel: intl.formatMessage(m.locationEmpty),
      manyLabel: (count) => intl.formatMessage(m.locationMany, { count }),
    },
    {
      key: 'languages',
      label: intl.formatMessage(m.languageLabel),
      options: languageOptions.map((o) => ({ value: o.id, label: o.label })),
      value: selectedLanguages,
      emptyLabel: intl.formatMessage(m.languageEmpty),
      manyLabel: (count) => intl.formatMessage(m.languageMany, { count }),
    },
    {
      key: 'tags',
      label: intl.formatMessage(m.categoryLabel),
      options: tags.map((o) => ({ value: o.id, label: o.name })),
      value: selectedTagIds,
      emptyLabel: intl.formatMessage(m.categoryEmpty),
      manyLabel: (count) => intl.formatMessage(m.categoryMany, { count }),
    },
  ];

  // Hide a filter when there's nothing useful to choose. A single option
  // (or zero) gives the visitor no way to narrow the feed, so the trigger
  // just adds noise to the bar.
  const showLocationFilter = locationOptions.length > 1;
  const showLanguageFilter = languageOptions.length > 1;
  const showCategoryFilter = tags.length > 1;
  const filterCount =
    (showLocationFilter ? 1 : 0) +
    (showLanguageFilter ? 1 : 0) +
    (showCategoryFilter ? 1 : 0);

  const filterChildren = (
    <>
      {showLocationFilter && (
        <LocationFilter
          value={selectedLocationIds}
          options={locationOptions}
          onChange={(locationIds) => setMultiSelect({ locations: locationIds })}
        />
      )}
      {showLanguageFilter && (
        <LanguageFilter
          value={selectedLanguages}
          options={languageOptions}
          onChange={(languages) => setMultiSelect({ languages })}
        />
      )}
      {showCategoryFilter && (
        <CategoryFilter
          value={selectedTagIds}
          options={tags}
          onChange={(tagIds) => setMultiSelect({ tags: tagIds })}
        />
      )}
    </>
  );

  return (
    <>
      <PageCard>
        <Stack gap={{ base: 'large', tablet: '5xlarge' }}>
          <Stack gap="xlarge">
            {/* Mobile: hero + "Start searching" button share a raised panel
              with rounded bottom corners. Bleeds horizontally to the
              viewport edges. Desktop: the panel is a passthrough — Hero
              stays a centered text block above an inline SearchBar. */}
            <div className={heroPanelStyles}>
              <Hero
                title={
                  <FormattedMessage
                    defaultMessage="Discover your options"
                    description="Hero heading on the storefront home page"
                  />
                }
                subtitle={
                  <FormattedMessage
                    defaultMessage="Browse upcoming experiences and find one that suits you."
                    description="Hero subtitle on the storefront home page"
                  />
                }
                badge={
                  rating && (
                    <RatingBadge
                      average={rating.average}
                      count={rating.count}
                    />
                  )
                }
              />
              <div className={mobileOnlyStyles}>
                <Button
                  appearance="secondary"
                  size="large"
                  onClick={() => setSheetOpen(true)}
                  className={startSearchingButtonStyles}
                >
                  <SearchIcon size="medium" />
                  <FormattedMessage
                    defaultMessage="Start searching"
                    description="Mobile-only button that opens the home page search dialog"
                  />
                </Button>
                {sheetOpen && (
                  <FilterSheet
                    title={
                      <FormattedMessage
                        defaultMessage="What are you looking for?"
                        description="Heading of the home page search dialog (mobile)"
                      />
                    }
                    submitLabel={
                      <FormattedMessage
                        defaultMessage="Search"
                        description="Primary action in the home page search dialog (mobile)"
                      />
                    }
                    fields={sheetFields}
                    onClose={() => setSheetOpen(false)}
                    onSubmit={(selection) => {
                      setMultiSelect({
                        locations: selection.locations ?? [],
                        languages: selection.languages ?? [],
                        tags: selection.tags ?? [],
                      });
                      setSheetOpen(false);
                    }}
                  />
                )}
              </div>
            </div>
            {filterCount > 0 && (
              <div className={desktopOnlyStyles}>
                <FilterBar filterCount={filterCount}>
                  {filterChildren}
                </FilterBar>
              </div>
            )}
          </Stack>

          <Stack gap={{ base: '5xlarge' }}>
            {visibleExperiences.length === 0 ? (
              experiences.length === 0 ? (
                <EmptyState />
              ) : (
                <EmptyState variant="filtered" onResetFilters={resetFilters} />
              )
            ) : (
              <ExperienceGrid count={visibleExperiences.length}>
                {visibleExperiences.map((e) => (
                  <li key={e.id}>
                    <ExperienceCard
                      id={e.id}
                      name={e.name}
                      image={e.image}
                      price={e.price}
                      locations={e.locations}
                      showFromPrice={preferences.showFromPrice}
                      ownerHostName={e.ownerHostName}
                      filterSearch={experienceFilterQs}
                      imageSizes={cardImageSizes}
                      imagePriority={priorityImages.has(e.id)}
                      datesSlot={
                        <ExperienceCardDates
                          experienceId={e.id}
                          datesByExperience={datesByExperience}
                        />
                      }
                    />
                  </li>
                ))}
              </ExperienceGrid>
            )}

            {preferences.showUpcoming && upcoming.length > 0 && (
              // Dim + `aria-busy` while the fetcher reloads so clicking a
              // filter gives instant visual feedback (US-10172). Mirrors
              // booking-panel's `revalidationPendingStyles`.
              <div
                aria-busy={upcomingPending || undefined}
                className={cx(upcomingPending && upcomingPendingStyles)}
              >
                <UpcomingDatesSection
                  variant="expanded"
                  events={upcoming}
                  seeAllHref={seeAllDatesHref}
                  seatCounterVisibility={preferences.seatCounter}
                />
              </div>
            )}

            <CrossSellSection
              giftCards={preferences.giftCards}
              punchcards={preferences.punchcards}
            />

            <HostCard
              header={rootLoaderData?.companyName}
              description={company.hostDescription}
              coverPhoto={company.coverImage}
            >
              {rootLoaderData?.companyName && (
                <LinkButton
                  href={`mailto:${rootLoaderData?.companyEmail}`}
                  appearance="secondary"
                  size="large"
                >
                  <FormattedMessage
                    defaultMessage="Contact us"
                    description="Action on the host card on the home page that opens a mailto link"
                  />
                  <ArrowUpRightIcon size="small" />
                </LinkButton>
              )}
            </HostCard>

            {reviews && (
              <ReviewsSection
                starRating={reviews.starRating}
                count={reviews.count}
                starCounts={reviews.starCounts}
                reviews={reviews.reviews}
                now={renderedAt}
              />
            )}
          </Stack>
        </Stack>
      </PageCard>

      {/* Rendered OUTSIDE PageCard so it sits on the outer page surface,
          matching how the experience details page renders the same block. */}
      {preferences.privateEventRequest && (
        <>
          <PrivateEventCta locale={params.locale} />
          <div
            className={cx(
              maxWidthContent,
              css({ px: { base: 'small', tablet: 'large' } })
            )}
          >
            <Divider />
          </div>
        </>
      )}
    </>
  );
}

const desktopOnlyStyles = css({
  display: { base: 'none', tablet: 'flex' },
  // Center the bar so the narrow (single-filter, 400px) variant doesn't
  // hug the left edge of the page card. The wide (two-filter) variant
  // still spans full width because `<SearchBar>` sets `width: full`.
  justifyContent: 'center',
});

const mobileOnlyStyles = css({
  display: { base: 'block', tablet: 'none' },
});

// Pending treatment while the upcoming-events fetcher reloads. Mirrors
// booking-panel's `revalidationPendingStyles` — dim and disable interaction so
// a filter click reads as "loading" immediately.
const upcomingPendingStyles = css({
  opacity: '[0.5]',
  pointerEvents: 'none',
});

const startSearchingButtonStyles = css({
  width: 'full',
});

const heroPanelStyles = css({
  // Mobile: raised panel that bleeds horizontally past the PageCard's
  // padding to meet the viewport edges, with rounded bottom corners only.
  // Desktop: visual chrome dissolves — Hero sits as a plain centered text
  // block above the inline SearchBar.
  background: { base: 'surface.raised', tablet: '[transparent]' },
  borderBottomLeftRadius: { base: 'large', tablet: 'none' },
  borderBottomRightRadius: { base: 'large', tablet: 'none' },
  marginX: { base: '-small', tablet: 'none' },
  paddingX: { base: 'small', tablet: 'none' },
  paddingBottom: { base: 'medium', tablet: 'none' },
});
